import { inject, Injectable, Injector } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, filter, map, Observable, of, switchMap, take, timeout } from 'rxjs';
import { TOKEN_VALIDATION, HostCommunicationService, ROUTE } from '../../feature-seatmap';
import { AuthStoreService } from './auth-store.service';
import { removeParamFromUrl } from 'utilities';
import { normalizeAppReturnUrl } from './auth-redirect.service';

/**
 * Whether `url` signals embedded mode via a `/embedded` path segment (as opposed
 * to the `?embedded=1` query param). Used to avoid pushing a browser history entry
 * for the initial post-login navigation into an embedded route, since the caller
 * (e.g. an iframe host) never expects a "back" step to land on the token URL.
 */
function isEmbeddedPath(url: string): boolean {
  const pathname = url.split('?')[0].split('#')[0];
  return pathname === '/embedded' || pathname.startsWith('/embedded/');
}

@Injectable()
export class PostAuthNavigation {
  private readonly authStoreService = inject(AuthStoreService);
  private readonly tokenValidation = inject<Observable<string | null>>(TOKEN_VALIDATION, {
    optional: true,
  });
  private readonly route = inject(ActivatedRoute);
  private readonly hostCommunicationService = inject(HostCommunicationService);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);

  performNavigation() {
    this.validateToken()
      ?.pipe(
        switchMap(urlToNavigate => {
          if (!urlToNavigate) {
            return of(null);
          }

          return this.waitForBimplusUserId().pipe(map(() => urlToNavigate));
        }),
        takeUntilDestroyed()
      )
      .subscribe({
        next: urlToNavigate => {
          if (!urlToNavigate) {
            return;
          }
          const target = normalizeAppReturnUrl(urlToNavigate) ?? '/';
          this.router.navigateByUrl(target, { replaceUrl: isEmbeddedPath(target) });
        },
      });
  }

  private validateToken() {
    return this.tokenValidation?.pipe(
      map(validToken => {
        if (!validToken) {
          return null;
        }
        this.hostCommunicationService.handleHostSource({
          token: validToken,
        });
        this.authStoreService.setAuthenticated({
          isAuthenticated: true,
          allConfigsAuthenticated: [],
        });

        const returnUrl = this.route.snapshot.queryParams['returnUrl'];
        if (returnUrl) {
          const cleanReturnUrl = removeParamFromUrl(returnUrl, 'token') ?? returnUrl;
          return normalizeAppReturnUrl(cleanReturnUrl) ?? '/';
        }

        const redirectTo = this.route.snapshot.queryParams['redirectTo'];
        if (redirectTo) {
          const cleanRedirectTo = removeParamFromUrl(redirectTo, 'token') ?? redirectTo;
          return normalizeAppReturnUrl(cleanRedirectTo) ?? '/';
        }

        const currentUrl = this.router.url ?? '';
        if (currentUrl.startsWith(`/${ROUTE.login}`)) {
          return '/';
        }

        // No explicit target outside login: keep current route unchanged for refresh flows.
        return null;
      })
    );
  }

  private waitForBimplusUserId() {
    const currentUserId = this.authStoreService.bimplusUserId();
    if (currentUserId.trim().length > 0) {
      return of(currentUserId);
    }

    return toObservable(this.authStoreService.bimplusUserId, { injector: this.injector }).pipe(
      filter((userId): userId is string => userId.trim().length > 0),
      take(1),
      timeout({ first: 1500 }),
      catchError(() => of(''))
    );
  }
}
